import { ChangeEvent, ClipboardEvent, KeyboardEvent, useEffect, useRef } from 'react'
import { cn } from '@cannect/lib/utils'

type CodeInputProps = {
  length?: number
  value: string
  onChange: (value: string) => void
  onComplete?: (value: string) => void
  ariaLabel: string
  id?: string
  autoFocus?: boolean
  disabled?: boolean
  error?: boolean
}

// Widget de código segmentado (1 caixa por dígito) — porte de
// manager-cannect/src/components/CodeInput (já validado na feature 2fa-auth
// daquele repo). Caixa é 44x44 no breakpoint base (alvo de toque >=44px nos
// dois eixos) e cresce pra 56x64 a partir de `sm` — o tamanho base foi
// calculado pro pior caso real (6 caixas dentro do TwoFactorLoginGate em
// 375px de viewport), não é um valor arbitrário.
// `role="group"` + `aria-label` no wrapper substitui o <fieldset><legend> pra
// leitor de tela anunciar o grupo como uma unidade, já que não há um
// <label htmlFor> único possível com N inputs.
// `appearance-none` é obrigatório aqui: o tailwind.config deste repo tem
// `preflight: false`, então o <input> mantém o chrome nativo do navegador
// (borda 3D/inset) por baixo da borda customizada, quebrando o border-radius
// visualmente. Mesmo motivo pelo qual Form/Input.tsx e Form/TextArea.tsx já
// usam essa classe.
export const CodeInput = ({
  length = 6,
  value,
  onChange,
  onComplete,
  ariaLabel,
  id,
  autoFocus,
  disabled,
  error
}: CodeInputProps) => {
  const refs = useRef<Array<HTMLInputElement | null>>([])
  const digits = Array.from({ length }, (_, i) => value[i] || '')

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const commit = (next: string) => {
    onChange(next)
    if (next.length === length) onComplete?.(next)
  }

  const handleChange = (index: number, raw: string) => {
    const digit = raw.replace(/\D/g, '').slice(-1)
    const chars = value.split('')
    chars[index] = digit
    const next = chars.join('').slice(0, length)
    commit(next)
    if (digit && index < length - 1) refs.current[index + 1]?.focus()
  }

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      event.preventDefault()
      const chars = value.split('')
      chars[index - 1] = ''
      commit(chars.join(''))
      refs.current[index - 1]?.focus()
    } else if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault()
      refs.current[index - 1]?.focus()
    } else if (event.key === 'ArrowRight' && index < length - 1) {
      event.preventDefault()
      refs.current[index + 1]?.focus()
    } else if (event.key === 'Enter') {
      // `onComplete` já submete sozinho no último dígito — deixar o Enter
      // borbulhar até o <form> disparasse um 2º submit logo em seguida.
      event.preventDefault()
    }
  }

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
    if (!pasted) return
    event.preventDefault()
    commit(pasted)
    refs.current[Math.min(pasted.length, length - 1)]?.focus()
  }

  // Extraído do cn(...) inline pra evitar ternário aninhado (regra
  // no-nested-ternary do lint) sem mudar o resultado: erro > preenchido >
  // vazio, na mesma ordem de prioridade de antes.
  const getBorderClass = (hasDigit: boolean) => {
    if (error) return 'border-red-600 focus-visible:ring-red-600'
    if (hasDigit) return 'border-primary-600 bg-white focus-visible:ring-primary-700'
    return 'border-gray-200 focus-visible:ring-primary-700'
  }

  return (
    <div role="group" aria-label={ariaLabel} id={id} className="flex gap-2 sm:gap-3">
      {digits.map((digit, index) => (
        <input
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          ref={(el) => {
            refs.current[index] = el
          }}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={digit}
          disabled={disabled}
          aria-label={`Dígito ${index + 1} de ${length}`}
          onChange={(event: ChangeEvent<HTMLInputElement>) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={handlePaste}
          className={cn(
            'h-11 w-11 appearance-none rounded-xl border-2 border-solid text-center text-lg font-semibold text-gray-900 shadow-sm outline-none transition-all',
            'sm:h-16 sm:w-14 sm:text-2xl',
            'bg-gray-50 focus-visible:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
            getBorderClass(Boolean(digit)),
            disabled && 'cursor-not-allowed opacity-50'
          )}
        />
      ))}
    </div>
  )
}
